import React, { useMemo, useState } from 'react';
import {
  Card,
  Col,
  DatePicker,
  Form,
  FormInstance,
  Radio,
  Row,
  Tabs,
} from 'antd';

import { toCurrency } from 'utils/utils';

import DataGrid, { DataGridColDef } from 'components/DataGrid/DataGrid';
import { useGetFlowsByCriteria } from '../useGetFlowsByCriteria';
import ParListSelect from 'components/ParListSelect';
import DF from 'DisplayFramework';
import Checkbox from 'components/Checkbox';
import { sumBy } from 'lodash';
import LateCharge from 'pages/ClientInvoice/LateCharge';
import TPContracts from './TPContracts';
import GetLabel from 'DisplayFramework/components/GetLabel';
import moment from 'moment';
import ModalController from 'components/ModalController';
import CIDetail from 'pages/ClientInvoice/CIDetail';
import PayementModal from 'pages/Payment/PayementModal';

const { TabPane } = Tabs;

export type FlowsProps = {
  tpid: String;
  showAsModal?: boolean;
  form: FormInstance;
  currcode?: string;
};

const Flows = (props: FlowsProps) => {
  const { tpid, showAsModal, form, currcode, } = props;
  const fieldName = 'flows';
  const [dateParams] = useState<any>({
    docdate: '',
    flagCN: true,
    flagPayment: true,
    flagInvoice: true,
    flagReject: true,
    currcode: '',
  });

  const [searchParams, setSearchParams] = useState<any>({
    docdate: '',
    flagCN: true,
    flagPayment: true,
    flagInvoice: true,
    flagReject: true,
    paystatus: '',
    tpid: tpid,
    currcode: currcode,
  });

  const [state] = useState<any>({
    flow: 'IN',
    currcode: currcode,
    totaldebit: 0,
    totalcredit: 0,
    totalreamining: 0,
    totalsuspense: 0,
  });
  const { data, isFetching } = useGetFlowsByCriteria(
    searchParams,
    searchParams,
    state.flow,
    state.currcode
  );
  if (data) {
    state.totaldebit = sumBy(data, 'debitvalidated');
    state.totalcredit = sumBy(data, 'creditvalidated');
    state.totalreamining = sumBy(data, 'remaining');
    state.totalsuspense = sumBy(data, 'suspense');
  } else {
    state.totaldebit = 0;
    state.totalcredit = 0;
    state.totalreamining = 0;
    state.totalsuspense = 0;
  }

  const columns = useMemo<DataGridColDef<any>[]>(
    () => [
      {
        dataIndex: 'docdate',
        filtrable: 'docdate',
        defaultSortOrder: 'descend',
        sorter: (a, b) => moment(a.docdate).unix() - moment(b.docdate).unix(),
        filterComponent: (
          <Form.Item
            noStyle
            name={[fieldName, form.getFieldValue('docdate'), 'docdate']}
          >
            <DatePicker
              name="docdate"
              // onChange={(e) => {
              //   if (e !== null) {
              //     dateParams.docdate = dateToString(e);
              //   } else {
              //     dateParams.docdate = '';
              //   }
              // }}
              onChange={(e, date) => {
                dateParams.docdate = date;
              }}
            />
          </Form.Item>
        ),

        width: '100px',
      },
      {
        comcode: 'currcode',
        dataIndex: 'currcode',
        filtrable: true,
        filterComponent: (
          <ParListSelect
            parcode="CURRENCY"
            onSelect={(e) => {
              state.currcode = e;
              search();
            }}
          />
        ),
        width: '80px',
      },

      {
        filtrable: true,
        filterMultiple: true,
        filterComponent: (
          <Row gutter={32}>
            <Col xs={8}>
              <Radio.Group defaultValue="IN">
                <Radio
                  value="IN"
                  onChange={(e) => (state.flow = e.target.value)}
                >
                  IN{' '}
                </Radio>
                <Radio
                  value="OUT"
                  onChange={(e) => (state.flow = e.target.value)}
                >
                  OUT
                </Radio>
                <Radio
                  value="BOTH"
                  onChange={(e) => (state.flow = e.target.value)}
                >
                  BOTH
                </Radio>
              </Radio.Group>
            </Col>

            <Col xs={4}>
              <Checkbox
                comcode="flagInvoice"
                defaultChecked
                onChange={(e) => (dateParams.flagInvoice = e.target.checked)}
                style={{ marginRight: 13 }}
              />
            </Col>
            <Col xs={4}>
              <Checkbox
                comcode="flagCN"
                defaultChecked
                onChange={(e) => (dateParams.flagCN = e.target.checked)}
              />
            </Col>

            <Col xs={4}>
              <Checkbox
                comcode="flagReject"
                defaultChecked
                onChange={(e) => (dateParams.flagReject = e.target.checked)}
              />
            </Col>
            <Col xs={4}>
              <Checkbox
                comcode="flagPayment"
                defaultChecked
                onChange={(e) => (dateParams.flagPayment = e.target.checked)}
              />
            </Col>
          </Row>
        ),
        comcode: 'flowtype',
        dataIndex: 'flowtypelabel',
        width: '150px',
      },

      {
        comcode: 'objectreference',
        dataIndex: 'objectreference',
        width: '150px',
        render: (value, record) => (
          <div>
            <ModalController>
              {({ setVisible, visible }) => (
                <>
                  <a
                    onClick={() => {
                      setVisible(true);
                    }}
                  >
                    {record.objectreference}
                  </a>
                  { record.flowtype === 'INVOICE' ? (
                    <CIDetail
                      ciid={Number(record.objectid)}
                      onCancel={() => setVisible(false)}
                      showAsModal
                      visible={visible}
                    />
                  ) : record.flowtype=== 'PAYMENT' ? (
                    <PayementModal
                      payid={Number(record.objectid)}
                      onCancel={() => setVisible(false)}
                      showAsModal
                      hideButtons
                      visible={visible}
                    />
                  ) : (
                    ''
                  )}
                </>
              )}
            </ModalController>
          </div>
        ),
      },

      {
        comcode: 'debitinprogress',
        dataIndex: 'debitinprogress',
        render: (val, record) => toCurrency(val, record.currcode ?? localStorage.getItem('currcode') ?? ''),

        width: '150px',
      },
      {
        comcode: 'debitvalidated',
        dataIndex: 'debitvalidated',
        render: (val, record) => toCurrency(val, record.currcode ?? localStorage.getItem('currcode') ?? ''),
        width: '150px',
      },
      {
        comcode: 'creditinprogress',
        dataIndex: 'creditinprogress',
        render: (val, record) => toCurrency(val, record.currcode ?? localStorage.getItem('currcode') ?? ''),

        width: '150px',
      },
      {
        comcode: 'creditvalidated',
        dataIndex: 'creditvalidated',
        render: (val, record) => toCurrency(val, record.currcode ?? localStorage.getItem('currcode') ?? ''),

        width: '150px',
      },
      {
        dataIndex: 'remaining',
        render: (val, record) => toCurrency(val, record.currcode ?? localStorage.getItem('currcode') ?? ''),

        width: '150px',
      },
      {
        dataIndex: 'suspense',
        render: (val, record) => toCurrency(val, record.currcode ?? localStorage.getItem('currcode') ?? ''),

        width: '150px',
      },

      {
        comcode: 'statuslabel',
        dataIndex: 'paystatuslabel',
        width: '150px',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateParams, state]
  );

  const search = () => {
    searchParams.docdate = dateParams.docdate;
    searchParams.flagCN = dateParams.flagCN;
    searchParams.flagPayment = dateParams.flagPayment;
    searchParams.flagInvoice = dateParams.flagInvoice;
    searchParams.flagReject = dateParams.flagReject;
    searchParams.currcode = state.currcode;

    return setSearchParams;
  };

  return (
    <DF.Provider pagcode="flow">
      <Card>
      <Tabs animated tabPosition="top" defaultActiveKey="1" >
        <TabPane key={1} tab={<GetLabel label="contract" />}>
          <Card bordered type="inner" style={{ height: '100%' }}>
            <TPContracts
              tpid={Number(tpid)}
              showAsModal={showAsModal}
              currcode={currcode}
            />
          </Card>
        </TabPane>
        <TabPane key={2} tab={<GetLabel label="Flows" />}>
          <DataGrid
            rowKey="objectid"
            header={
              <Row
                justify="end"
                style={{
                  fontSize: '15px',
                  fontWeight: 'bold',
                  color: '#00a896',
                }}
                gutter={30}
              >
                <Col span={15}>
                  <Row justify="space-around">
                    <Col>
                      <span>
                        <GetLabel label='Debit'/> : {toCurrency(state.totaldebit, state.currcode)}
                      </span>
                    </Col>
                    <Col>
                      <span>
                          <GetLabel label='Credit'/> : {toCurrency(state.totalcredit, state.currcode)}
                      </span>
                    </Col>

                    <Col>
                      <span>
                      <GetLabel label=' Remaining'/> :{' '}
                        {toCurrency(state.totalreamining, state.currcode)}
                      </span>
                    </Col>
                    <Col>
                      <span>
                         <GetLabel label='Suspense'/> :{' '}
                        {toCurrency(state.totalsuspense, state.currcode)}
                      </span>
                    </Col>
                  </Row>
                </Col>
                {/* 
                <Col>
                  <Space size={24} style={{ fontSize: '17px' }}></Space>
                </Col> */}
              </Row>
            }
            columns={columns}
            dataSource={data}
            onSearch={search()}
            spanFilter={[4, 2, 18]}
            pagination={{ pageSize: 200 }}
            scroll={{ x: 1000, y: 400 }}
            initialSearchValues={searchParams}
            isLoading={isFetching}
            // extractData="Flows"
          />
        </TabPane>
        <TabPane key={3} tab={<GetLabel label="Late charge" />}>
          <Card bordered type="inner" style={{ height: '100%' }}>
            <LateCharge
              tpid={Number(tpid)}
              formG={form}
              showAsModal={showAsModal}
            />
          </Card>
        </TabPane>
      </Tabs>
      </Card>
    </DF.Provider>
  );
};

export default Flows;
